<template>
  <div class="manual">
    <aside class="toc">
      <div class="toc-title">目录</div>
      <a
        v-for="s in sections"
        :key="s.id"
        :class="{ active: activeId === s.id }"
        href="javascript:;"
        @click="scrollTo(s.id)"
      >{{ s.title }}</a>
    </aside>
    <div class="manual-body" ref="body">
      <div class="intro-box">
        <h2>用户操作手册</h2>
        <p>按业务上手顺序编写，覆盖 PC 管理端、现场 App、业主小程序。建议首次使用时按章节顺序操作一遍。</p>
        <div class="quick">
          <div class="quick-title">快速上手（半天跑通主线）</div>
          <ol>
            <li>PC：公司信息与用户角色 → 工序 / 房型 / 评价 → 供应商、材料价格、工人价格</li>
            <li>PC：创建项目（选房型生成计划）→ 生成项目邀请码发给业主</li>
            <li>App 项目经理：排期派工、工序开工/完工、发通知、材料收退、工资申请</li>
            <li>小程序业主：扫码绑定 → 看进展 → 确认通知 → 评价</li>
            <li>PC：审批材料/工资单据，处理售后与投诉</li>
          </ol>
        </div>
      </div>

      <section v-for="s in sections" :id="s.id" :key="s.id" class="chapter">
        <h3>{{ s.title }}</h3>
        <div v-for="(block, bi) in s.blocks" :key="bi" class="block">
          <h4 v-if="block.heading">{{ block.heading }}</h4>
          <p v-if="block.path" class="path">菜单位置：{{ block.path }}</p>
          <p v-if="block.text" class="text">{{ block.text }}</p>
          <ol v-if="block.steps && block.steps.length">
            <li v-for="(t, i) in block.steps" :key="'s' + i">{{ t }}</li>
          </ol>
          <ul v-if="block.bullets && block.bullets.length">
            <li v-for="(t, i) in block.bullets" :key="'b' + i">{{ t }}</li>
          </ul>
          <p v-if="block.tip" class="tip">提示：{{ block.tip }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
const sections = [
  {
    id: 'ch1',
    title: '一、认识三端与账号',
    blocks: [
      {
        heading: '1.1 产品定位',
        text: '华锐智企云面向装饰公司，覆盖内部管理、供应链协同、项目经理移动作业、客户服务与品牌宣传。'
      },
      {
        heading: '1.2 如何获取各端',
        bullets: [
          'PC：浏览器打开 https://erp.harence.com ，用企业账号登录',
          'App：PC 登录页扫描「扫码下载 App」；微信扫码后按提示用浏览器打开，再选安卓/iOS 下载',
          '小程序：微信搜索「华锐智企云」（以实际发布名为准），或扫描企业邀请小程序码进入'
        ]
      },
      {
        heading: '1.3 账号类型',
        bullets: [
          '企业管理员 / 员工：PC + App（能力取决于角色菜单与项目授权）',
          '工人：在「基础资料 → 工人管理」开通 App 账号',
          '供应商：在「基础资料 → 供应商」开通后使用供应商工作台或 App',
          '业主：无需开账号，通过邀请码在小程序绑定即可'
        ],
        tip: '首次登录 PC 需搜索选择企业；忘记密码可用登录页短信重置。'
      }
    ]
  },
  {
    id: 'ch2',
    title: '二、开户与组织准备（PC）',
    blocks: [
      {
        heading: '2.1 公司信息',
        path: '系统管理 → 公司信息',
        steps: [
          '上传 Logo，填写公司名称、联系人、电话、地址等并保存',
          '填写发票信息（抬头、税号等），申请开票时自动带出'
        ],
        tip: '开票仅支持 1% 增值税普通发票。'
      },
      {
        heading: '2.2 部门、角色、用户',
        path: '系统管理 → 部门管理 / 角色管理 / 用户管理',
        text: '建议顺序：部门 → 角色勾选菜单权限 → 用户分配角色。',
        bullets: [
          '部门：新增编码、名称、上级后保存',
          '角色：新增后打开菜单权限勾选可见功能',
          '用户：选部门、角色，设置登录账号后保存；可重置密码'
        ]
      },
      {
        heading: '2.3 套餐订阅与开票',
        path: '套餐订阅',
        steps: [
          '查看当前套餐与权益',
          '对公转账后点「我已转账」提交认款，平台到账后开通',
          '订阅历史中可「申请开票」，开票完成后「下载发票」'
        ]
      }
    ]
  },
  {
    id: 'ch3',
    title: '三、基础配置（建项前）',
    blocks: [
      {
        heading: '3.1 工序设置',
        path: '项目管理 → 工序设置',
        steps: ['新增工序 → 选阶段、填名称', '需要业主评价时关联评价模板', '保存，供房型组合使用']
      },
      {
        heading: '3.2 评价设置',
        path: '项目管理 → 评价设置',
        steps: ['新增模板 → 名称与评分项（1～5 星）', '保存后在工序设置中挂到对应工序']
      },
      {
        heading: '3.3 房型设置',
        path: '项目管理 → 房型设置',
        steps: [
          '确认已有工序',
          '新增房型 → 添加工序并设置起止天数',
          '保存；建项目选该房型可自动生成计划'
        ]
      },
      {
        heading: '3.4 供应商与材料',
        path: '基础资料 → 供应商 / 材料管理 / 材料价格表',
        bullets: [
          '供应商：建档；需协同时点「开通」账号',
          '材料：先分类再新增材料（规格、单位、启用）',
          '材料价格表：选供应商、加材料行与单价（申请按此取价）'
        ]
      },
      {
        heading: '3.5 工人与人工价格',
        path: '基础资料 → 工人管理 / 工人价格表',
        bullets: [
          '工人管理：新增工人；上 App 时点「开通」',
          '工人价格表：维护工种/单价并启用（工资申请按此计价）'
        ]
      }
    ]
  },
  {
    id: 'ch4',
    title: '四、创建项目与邀请业主',
    blocks: [
      {
        heading: '4.1 新建项目',
        path: '项目管理 → 项目档案',
        steps: [
          '新建项目 → 填客户、地址、房型、项目经理、开工日期等 → 保存',
          '选了房型会自动生成计划；进入详情可完善档案与各页签'
        ]
      },
      {
        heading: '4.2 项目计划',
        path: '项目管理 → 项目计划',
        bullets: [
          '可筛选、导出；工序可「开始」「完成」',
          '未开始工序可编辑/删除；可关注延期与工人确认状态',
          '现场更常用 App 操作，PC 适合统筹查看'
        ]
      },
      {
        heading: '4.3 邀请码',
        path: '运营管理 → 邀请码',
        steps: [
          '生成邀请码 → 绑定类型选「公司」或「项目」',
          '下载邀请小程序码发给业主扫码或输码绑定'
        ],
        tip: '公司码看活动/案例；项目码还可看进度、通知、评价、售后。'
      },
      {
        heading: '4.4 项目签到（PC）',
        path: '项目管理 → 项目签到',
        text: '用于查看现场 App 打卡记录；签到请在 App 项目详情操作。'
      }
    ]
  },
  {
    id: 'ch5',
    title: '五、材料业务（PC + App）',
    blocks: [
      {
        text: '主线：价格表 → 申请 → 审批 → 送货 → 收货 →（可选）退货。'
      },
      {
        heading: '5.1 材料申请',
        path: '材料管理 → 材料申请',
        steps: [
          '新建 → 选项目/工序/供应商/明细 → 提交',
          '有权限者审批通过或驳回；可在「申请明细」汇总查询'
        ],
        tip: 'App：项目详情 → 材料管理 → 新建申请。'
      },
      {
        heading: '5.2 收货',
        path: '材料管理 → 收货单',
        bullets: [
          '申请已审批且未收齐时可收货',
          '推荐 App 项目详情「新建收货」核对数量后确认'
        ]
      },
      {
        heading: '5.3 退货',
        path: '材料管理 → 材料退货',
        steps: ['新建退货 → 关联已收材料 → 提交审批', 'App 亦可在项目详情发起']
      },
      {
        heading: '5.4 供应商协同',
        bullets: [
          'PC「供应商工作台」查看申请/收货/退货',
          'App 查看待送货、申请详情与退货协同'
        ]
      }
    ]
  },
  {
    id: 'ch6',
    title: '六、人工与工资',
    blocks: [
      {
        text: '主线：价格表 → 排期派工 → 工期确认 → 工资申请 → 审批 → 确认发放。'
      },
      {
        heading: '6.1 排期派工（App 项目经理）',
        steps: [
          '打开「排期」或项目「项目排期」',
          '选择工序/日期添加工人',
          '工人在「待确认工期」确认；可在日历查看安排'
        ]
      },
      {
        heading: '6.2 工资申请',
        path: '人工管理 → 工资申请',
        steps: [
          '新建 → 选项目与工人明细 → 提交（App 亦可新建）',
          'PC 审批通过后「确认发放」（谨慎，不宜随意撤销）',
          '「工资明细」可跨单查询'
        ]
      },
      {
        heading: '6.3 工人端',
        bullets: ['我的工资、考勤、签到、现场照片、只读计划、通知']
      }
    ]
  },
  {
    id: 'ch7',
    title: '七、施工履约与通知（App）',
    blocks: [
      {
        heading: '7.1 工序开工/完工',
        steps: [
          '我的项目 → 项目详情',
          '对工序点「开始」或「完成」，业主小程序可同步看进展'
        ]
      },
      {
        heading: '7.2 发起通知',
        steps: [
          '项目内发起通知 → 选接收人/确认人 → 提交',
          '业主在小程序「我的通知」确认；PC 可查看确认状态'
        ],
        tip: '业主确认前建议先绑定手机号。'
      },
      {
        heading: '7.3 签到与照片',
        bullets: [
          '项目详情「签到」打卡',
          '现场照片 → 发布；业主可在项目动态查看'
        ]
      },
      {
        heading: '7.4 工作台',
        text: '汇总待办、未读通知、待确认工资等，便于每日开工前浏览。'
      }
    ]
  },
  {
    id: 'ch8',
    title: '八、业主小程序',
    blocks: [
      {
        heading: '8.1 绑定',
        steps: [
          '首页引导或「我的 → 绑定邀请码」输码/扫码确认',
          '可切换已绑定公司；建议绑定手机号'
        ]
      },
      {
        heading: '8.2 看进度',
        steps: ['我的项目 → 项目进展 / 成员 / 附件 / 动态（照片）']
      },
      {
        heading: '8.3 通知与评价',
        steps: [
          '我的通知 → 详情 → 确认',
          '可评价节点点「去评价」→ 按模板打分；在「我的评价」查看'
        ]
      },
      {
        heading: '8.4 售后与建议',
        steps: [
          '我的售后 → 新建（需绑项目）→ 跟踪处理',
          '投诉建议 → 提交后等待装企 PC 回复'
        ]
      },
      {
        heading: '8.5 案例活动内容',
        bullets: [
          '首页活动轮播与栏目内容；案例 Tab 浏览案例',
          '我的收藏查看已收藏内容'
        ]
      }
    ]
  },
  {
    id: 'ch9',
    title: '九、客户服务与运营（PC）',
    blocks: [
      {
        heading: '9.1 售后 / 投诉建议',
        path: '客户服务 → 售后管理 / 投诉建议',
        steps: ['按状态筛选 → 处理 → 更新状态、说明、图片 → 保存']
      },
      {
        heading: '9.2 运营管理',
        path: '运营管理 → 活动 / 内容 / 案例 / 活动报名 / 邀请码',
        bullets: [
          '活动、内容、案例启用后在小程序展示',
          '活动报名查看业主提交信息',
          '邀请码见第四章'
        ]
      }
    ]
  },
  {
    id: 'ch10',
    title: '十、统计分析',
    blocks: [
      {
        path: '统计分析 → 项目成本 / 材料销量 / 工人工资 / 供应商销量',
        steps: ['设筛选条件 → 查询 → 需要时导出 Excel'],
        text: '用于经营复盘，不替代日常单据操作。'
      }
    ]
  },
  {
    id: 'ch11',
    title: '十一、分角色每日清单',
    blocks: [
      {
        heading: '企业管理员（PC）',
        bullets: ['审材料/退货/工资', '处理售后与投诉', '关注套餐与账号数', '维护运营与邀请码']
      },
      {
        heading: '项目经理（App）',
        bullets: ['看工作台待办', '排期派工', '工序推进与通知', '材料收退与工资', '签到拍照']
      },
      {
        heading: '工人（App）',
        bullets: ['确认工期', '签到', '看通知与工资']
      },
      {
        heading: '供应商',
        bullets: ['看待送货申请', '送货并关注收货', '协同退货']
      },
      {
        heading: '业主（小程序）',
        bullets: ['看进展与动态', '确认通知与评价', '售后/投诉']
      }
    ]
  },
  {
    id: 'ch12',
    title: '十二、常见问题',
    blocks: [
      {
        bullets: [
          '选不到企业：企业框输入至少 2 个字搜索后再选',
          '建项无计划：确认已配房型且建项时选了房型',
          '业主看不到项目：需项目类型邀请码并完成绑定',
          '无法确认通知：先绑手机号，并确认接收人正确',
          '无法收货：申请须已审批且未收齐',
          '工人无派工：账号已开通且已加入排期，并在待确认工期确认',
          '供应商无申请：申请选对供应商且已审批，用开通账号登录',
          '下载 App：登录页扫码 → 浏览器打开 → 选安卓/iOS',
          '开票：套餐订阅历史查看开票中/下载发票'
        ]
      }
    ]
  }
]

export default {
  name: 'HelpUserManual',
  data() {
    return {
      sections,
      activeId: 'ch1'
    }
  },
  mounted() {
    const q = this.$route.query.section
    if (q && sections.some(s => s.id === q)) {
      this.$nextTick(() => this.scrollTo(q))
    }
  },
  methods: {
    scrollTo(id) {
      this.activeId = id
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.manual {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  max-width: 1100px;
}
.toc {
  position: sticky;
  top: 0;
  flex: 0 0 168px;
  max-height: calc(100vh - 160px);
  overflow: auto;
  padding: 12px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.toc-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  padding: 0 6px;
}
.toc a {
  display: block;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
  text-decoration: none;
  line-height: 1.4;
  margin-bottom: 2px;
}
.toc a:hover { background: #f1f5f9; color: #0f172a; }
.toc a.active {
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
}
.manual-body { flex: 1; min-width: 0; }
.intro-box {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 14px;
}
.intro-box h2 {
  margin: 0 0 6px;
  font-size: 20px;
  color: #0f172a;
}
.intro-box > p {
  margin: 0 0 14px;
  color: #64748b;
  font-size: 14px;
}
.quick {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.quick-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}
.quick ol {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.chapter {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px 8px;
  margin-bottom: 12px;
  scroll-margin-top: 12px;
}
.chapter h3 {
  margin: 0 0 12px;
  font-size: 16px;
  color: #0f172a;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}
.block { margin-bottom: 14px; }
.block h4 {
  margin: 0 0 6px;
  font-size: 14px;
  color: #0f172a;
}
.path {
  margin: 0 0 6px;
  font-size: 12px;
  color: #2563eb;
  font-weight: 600;
}
.text {
  margin: 0 0 6px;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
}
.block ol, .block ul {
  margin: 0 0 6px;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.tip {
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 12px;
  line-height: 1.5;
}
@media (max-width: 900px) {
  .manual { flex-direction: column; }
  .toc {
    position: static;
    flex: none;
    width: 100%;
    max-height: none;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .toc-title { width: 100%; }
  .toc a { display: inline-block; }
}
</style>
